import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import { useMaterialUIController } from "context";
import axios from "axios";
import API_ENDPOINTS from "config/api";

export default function Clientes() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [clientes, setClientes] = useState({ columns: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [busqueda, setBusqueda] = useState("");

  // Modales de confirmación y éxito
  const [modalExito, setModalExito] = useState({ open: false, mensaje: "" });
  const [modalError, setModalError] = useState({ open: false, mensaje: "" });
  const [modalEliminar, setModalEliminar] = useState(false);

  const [formData, setFormData] = useState({
    empresa_id: 1,
    tipo_documento: "CI",
    rif: "",
    nombre: "",
    email: "",
    telefono: "",
    direccion: "",
    tipo: "minorista",
    limite_credito: 0,
    estatus: "activo",
  });

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    try {
      setLoading(true);
      const params = {};
      if (busqueda) params.search = busqueda;

      const response = await axios.get(API_ENDPOINTS.CLIENTES, { params });
      const clientesData = response.data;

      const rows = clientesData.map((cliente) => ({
        cliente: (
          <MDBox>
            <MDTypography variant="button" fontWeight="bold" fontSize="0.85rem" display="block">
              {cliente.nombre}
            </MDTypography>
            <MDTypography variant="caption" color="text" fontSize="0.75rem">
              {cliente.tipo_documento}: {cliente.rif}
            </MDTypography>
          </MDBox>
        ),
        contacto: (
          <MDBox>
            <MDTypography variant="caption" color="text" fontSize="0.75rem" display="block">
              {cliente.email || "Sin email"}
            </MDTypography>
            <MDTypography variant="caption" color="text" fontSize="0.75rem">
              {cliente.telefono || "Sin teléfono"}
            </MDTypography>
          </MDBox>
        ),
        tipo: (
          <Chip
            label={cliente.tipo?.charAt(0).toUpperCase() + cliente.tipo?.slice(1) || "Minorista"}
            size="small"
            sx={{ fontSize: "0.7rem", height: "20px" }}
            color={
              cliente.tipo === "mayorista"
                ? "primary"
                : cliente.tipo === "corporativo"
                ? "secondary"
                : "default"
            }
          />
        ),
        estatus: (
          <Chip
            label={cliente.estatus === "activo" ? "Activo" : "Inactivo"}
            size="small"
            sx={{ fontSize: "0.7rem", height: "20px" }}
            color={cliente.estatus === "activo" ? "success" : "error"}
          />
        ),
        accion: (
          <IconButton onClick={(e) => handleOpenMenu(e, cliente)} size="small">
            <Icon fontSize="small">more_vert</Icon>
          </IconButton>
        ),
        _rawData: cliente,
      }));

      const columns = [
        { Header: "Cliente", accessor: "cliente", width: "40%" },
        { Header: "Contacto", accessor: "contacto", width: "30%" },
        { Header: "Tipo", accessor: "tipo", width: "12%" },
        { Header: "Estado", accessor: "estatus", width: "12%" },
        { Header: "", accessor: "accion", width: "6%" },
      ];

      setClientes({ columns, rows });
      setLoading(false);
    } catch (error) {
      console.error("Error al cargar clientes:", error);
      setLoading(false);
    }
  };

  const handleOpenMenu = (event, cliente) => {
    setMenuAnchor(event.currentTarget);
    setClienteSeleccionado(cliente);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleOpenModal = (editar = false) => {
    setModoEdicion(editar);
    if (!editar) {
      setFormData({
        empresa_id: 1,
        tipo_documento: "CI",
        numero_documento: "",
        nombre: "",
        apellido: "",
        razon_social: "",
        email: "",
        telefono: "",
        direccion: "",
        ciudad: "",
        estado: "",
        codigo_postal: "",
        tipo_cliente: "minorista",
        limite_credito: 0,
        estatus: "activo",
        notas: "",
      });
    } else if (clienteSeleccionado) {
      setFormData(clienteSeleccionado);
    }
    setOpenModal(true);
    handleCloseMenu();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        limite_credito: Number(formData.limite_credito) || 0,
        empresa_id: Number(formData.empresa_id) || 1,
      };

      if (modoEdicion) {
        await axios.put(API_ENDPOINTS.CLIENTE(clienteSeleccionado.id), payload);
        setModalExito({
          open: true,
          mensaje: "Cliente actualizado exitosamente",
        });
      } else {
        await axios.post(API_ENDPOINTS.CLIENTES, payload);
        setModalExito({
          open: true,
          mensaje: "Cliente creado exitosamente",
        });
      }
      handleCloseModal();
      fetchClientes();
    } catch (error) {
      console.error("Error al guardar cliente:", error);
      setModalError({
        open: true,
        mensaje: error.response?.data?.message || error.message || "Error al guardar cliente",
      });
    }
  };

  const abrirModalEliminar = () => {
    setModalEliminar(true);
    handleCloseMenu();
  };

  const handleDelete = async () => {
    if (clienteSeleccionado) {
      try {
        await axios.delete(API_ENDPOINTS.CLIENTE(clienteSeleccionado.id));
        setModalEliminar(false);
        setModalExito({
          open: true,
          mensaje: "Cliente eliminado exitosamente",
        });
        fetchClientes();
      } catch (error) {
        console.error("Error al eliminar cliente:", error);
        setModalEliminar(false);
        setModalError({
          open: true,
          mensaje: "Error al eliminar cliente",
        });
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <MDBox>
      <Card sx={{ mb: 2 }}>
        <MDBox p={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <MDButton
                variant="gradient"
                color={sidenavColor}
                onClick={() => handleOpenModal(false)}
                fullWidth
              >
                <Icon sx={{ mr: 1 }}>person_add</Icon>
                Nuevo Cliente
              </MDButton>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                placeholder="Buscar cliente..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && fetchClientes()}
                size="small"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={fetchClientes} size="small">
                      <Icon>search</Icon>
                    </IconButton>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <Card>
        <MDBox p={{ xs: 2, sm: 3 }}>
          <MDTypography variant="h6" fontWeight="bold" mb={2}>
            Lista de Clientes
          </MDTypography>
          {loading ? (
            <MDBox py={4} textAlign="center">
              <MDTypography variant="body2" color="text">
                Cargando clientes...
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox sx={{ overflowX: "auto" }}>
              <DataTable
                table={clientes}
                isSorted={false}
                entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20] }}
                showTotalEntries={true}
                noEndBorder
              />
            </MDBox>
          )}
        </MDBox>
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleOpenModal(true)}>
          <Icon sx={{ mr: 1 }}>edit</Icon>
          Editar
        </MenuItem>
        <MenuItem onClick={abrirModalEliminar} sx={{ color: "error.main" }}>
          <Icon sx={{ mr: 1 }}>delete</Icon>
          Eliminar
        </MenuItem>
      </Menu>

      <Modal open={openModal} onClose={handleCloseModal}>
        <MDBox
          component="form"
          onSubmit={handleSubmit}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "600px" },
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 4,
          }}
        >
          <MDBox
            bgColor={sidenavColor}
            borderRadius="8px"
            p={2}
            mb={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              {modoEdicion ? "Editar Cliente" : "Nuevo Cliente"}
            </MDTypography>
            <IconButton onClick={handleCloseModal} sx={{ color: "white" }}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Tipo Doc.</InputLabel>
                <Select
                  name="tipo_documento"
                  value={formData.tipo_documento}
                  onChange={handleInputChange}
                  label="Tipo Doc."
                  required
                >
                  <MenuItem value="CI">CI</MenuItem>
                  <MenuItem value="RIF">RIF</MenuItem>
                  <MenuItem value="Pasaporte">Pasaporte</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <MDInput
                name="rif"
                label="RIF / Documento"
                value={formData.rif}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12}>
              <MDInput
                name="nombre"
                label="Nombre Completo"
                value={formData.nombre}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                name="email"
                type="email"
                label="Email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                name="telefono"
                label="Teléfono"
                value={formData.telefono}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <MDInput
                name="direccion"
                label="Dirección"
                value={formData.direccion}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Cliente</InputLabel>
                <Select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleInputChange}
                  label="Tipo de Cliente"
                >
                  <MenuItem value="minorista">Minorista</MenuItem>
                  <MenuItem value="mayorista">Mayorista</MenuItem>
                  <MenuItem value="corporativo">Corporativo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Estatus</InputLabel>
                <Select
                  name="estatus"
                  value={formData.estatus}
                  onChange={handleInputChange}
                  label="Estatus"
                >
                  <MenuItem value="activo">Activo</MenuItem>
                  <MenuItem value="inactivo">Inactivo</MenuItem>
                  <MenuItem value="suspendido">Suspendido</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <MDInput
                name="notas"
                label="Notas"
                value={formData.notas}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          <MDBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <MDButton variant="outlined" color="secondary" onClick={handleCloseModal}>
              Cancelar
            </MDButton>
            <MDButton variant="gradient" color={sidenavColor} type="submit">
              {modoEdicion ? "Actualizar" : "Guardar"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Modal>

      {/* Modal de Éxito */}
      <Dialog
        open={modalExito.open}
        onClose={() => setModalExito({ open: false, mensaje: "" })}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: { xs: "90%", sm: 400 },
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon fontSize="large" color="success">
              check_circle
            </Icon>
            <MDTypography variant="h5" fontWeight="medium">
              ¡Éxito!
            </MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
            {modalExito.mensaje}
          </MDTypography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="gradient"
            color="success"
            onClick={() => setModalExito({ open: false, mensaje: "" })}
            fullWidth
          >
            <Icon sx={{ mr: 1 }}>check</Icon>
            Aceptar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Modal de Error */}
      <Dialog
        open={modalError.open}
        onClose={() => setModalError({ open: false, mensaje: "" })}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: { xs: "90%", sm: 400 },
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon fontSize="large" color="error">
              error
            </Icon>
            <MDTypography variant="h5" fontWeight="medium">
              Error
            </MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
            {modalError.mensaje}
          </MDTypography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="gradient"
            color="error"
            onClick={() => setModalError({ open: false, mensaje: "" })}
            fullWidth
          >
            <Icon sx={{ mr: 1 }}>close</Icon>
            Cerrar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Modal Eliminar Cliente */}
      <Dialog
        open={modalEliminar}
        onClose={() => setModalEliminar(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: { xs: "90%", sm: 400 },
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon fontSize="large" color="error">
              warning
            </Icon>
            <MDTypography variant="h5" fontWeight="medium">
              Eliminar Cliente
            </MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
            ¿Está seguro que desea eliminar el siguiente cliente?
          </MDTypography>

          {clienteSeleccionado && (
            <Card
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "rgba(244, 67, 54, 0.1)",
                border: "1px solid rgba(244, 67, 54, 0.3)",
              }}
            >
              <MDTypography variant="h6" fontWeight="bold" color="dark">
                {clienteSeleccionado.nombre}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {clienteSeleccionado.tipo_documento}: {clienteSeleccionado.rif}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {clienteSeleccionado.email || "Sin email"}
              </MDTypography>
            </Card>
          )}

          <MDTypography variant="caption" color="error" sx={{ mt: 2, display: "block" }}>
            <strong>Advertencia:</strong> Esta acción no se puede deshacer.
          </MDTypography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" color="dark" onClick={() => setModalEliminar(false)}>
            <Icon sx={{ mr: 1 }}>close</Icon>
            Cancelar
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleDelete}>
            <Icon sx={{ mr: 1 }}>delete</Icon>
            Eliminar
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
